import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { venuesAPI } from '../api/endpoints';
import type { VenueSearchParams } from '../types';
import { toApiParams } from '../utils/venuePause';

const DEFAULT_PAGE_SIZE = 100;

/**
 * The status shown by default: the venues still worth writing to — the open ones and the ones whose
 * status was never established. Closed ones and the places that have stopped programming concerts
 * stay on file but are not what the page opens on.
 */
export const DEFAULT_STATUS_FILTER = 'open,unknown';

/**
 * Whether a `status` param is exactly the default set, whichever order the boxes were ticked in:
 * the column filter joins the values in click order, so comparing the strings would read
 * `unknown,open` as a filter the user picked.
 */
export function isDefaultStatusFilter(value: unknown): boolean {
  if (typeof value !== 'string' || !value) return false;
  const picked = value.split(',');
  const defaults = DEFAULT_STATUS_FILTER.split(',');
  return picked.length === defaults.length && defaults.every((s) => picked.includes(s));
}

export function useVenueSearch() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Arriving on the page with nothing in the URL shows the open venues rather than every venue
  // ever filed. This is a default, not a locked filter: the first param the user writes (including
  // unticking Open, or "Reset to All") hands the URL full authority, so an emptied URL is then
  // taken at face value instead of defaulting again. See docs/VENUES_PAGE.md.
  const [defaultsApply, setDefaultsApply] = useState(true);
  const useDefaults = defaultsApply && Array.from(searchParams.keys()).length === 0;

  // Keep the default visible in the URL, so a reload or a shared link reproduces what is shown.
  useEffect(() => {
    if (!useDefaults) return;
    const next = new URLSearchParams();
    next.set('status', DEFAULT_STATUS_FILTER);
    setSearchParams(next, { replace: true });
  }, [useDefaults, setSearchParams]);

  // Parse URL params
  const params: VenueSearchParams = {
    q: searchParams.get('q')?.trim() || undefined,
    type: searchParams.get('type') || undefined,
    interest_level: searchParams.get('interest_level') || undefined,
    interest_band: searchParams.get('interest_band') || undefined,
    style: searchParams.get('style') || undefined,
    contact_filter: searchParams.get('contact_filter') || undefined,
    address_filter: searchParams.get('address_filter') || undefined,
    ideal_contact_period: searchParams.get('ideal_contact_period') || undefined,
    concert_period: searchParams.get('concert_period') || undefined,
    sent_count: searchParams.get('sent_count') || undefined,
    received_count: searchParams.get('received_count') || undefined,
    favorites: searchParams.get('favorites') || undefined,
    status: (useDefaults ? DEFAULT_STATUS_FILTER : searchParams.get('status')) || undefined,
    visibility: searchParams.get('visibility') || undefined,
    last_contact_after: searchParams.get('last_contact_after') || undefined,
    last_contact_before: searchParams.get('last_contact_before') || undefined,
    last_contact_never: searchParams.get('last_contact_never') || undefined,
    source: searchParams.get('source') || undefined,
    flagged: searchParams.get('flagged') || undefined,
    // Hidden by default: the Venues page shows who may be written to now. 'all' is the explicit
    // "no filter" the hourglass menu writes — without the sentinel this default would come straight
    // back every time the user turned it off. See docs/VENUE_PAUSE.md.
    paused: (searchParams.get('paused') as VenueSearchParams['paused']) || 'false',
    sort_by: searchParams.get('sort_by') || undefined,
    sort_order: (searchParams.get('sort_order') as 'asc' | 'desc') || undefined,
    page: Number(searchParams.get('page')) || 1,
    page_size: Number(searchParams.get('page_size')) || DEFAULT_PAGE_SIZE,
  };

  // Query
  const query = useQuery({
    queryKey: ['venues', params],
    queryFn: async ({ signal }) => {
      const response = await venuesAPI.list(toApiParams(params), signal);
      return response.data;
    },
    placeholderData: (previousData) => previousData,
    staleTime: 30000,
  });

  // Update URL params helper
  const updateParams = (updates: Partial<VenueSearchParams>) => {
    setDefaultsApply(false);
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === undefined || value === '' || value === null) {
        newParams.delete(key);
      } else {
        newParams.set(key, String(value));
      }
    });
    setSearchParams(newParams);
  };

  // Convenience methods
  // Trim before it reaches the URL: surrounding whitespace is never part of the term, and
  // keeping it would re-key the query (and refetch) on a stray trailing space.
  const setSearch = (q: string) => updateParams({ q: q.trim() || undefined, page: 1 });
  const setFilter = (key: string, value: string | undefined) =>
    updateParams({ [key]: value, page: 1 });
  const setFilters = (filters: Record<string, string | undefined>) =>
    updateParams({ ...filters, page: 1 });
  const setSort = (sort_by: string, sort_order: 'asc' | 'desc' | null) =>
    updateParams({ sort_by: sort_order ? sort_by : undefined, sort_order: sort_order ?? undefined });
  const setPage = (page: number) => updateParams({ page });
  const resetFilters = () => {
    setDefaultsApply(false);
    setSearchParams(new URLSearchParams());
  };

  const setAllParams = (newParams: Record<string, string | undefined>) => {
    setDefaultsApply(false);
    const urlParams = new URLSearchParams();
    Object.entries(newParams).forEach(([key, value]) => {
      if (value !== undefined && value !== '' && value !== null) {
        urlParams.set(key, value);
      }
    });
    setSearchParams(urlParams);
  };

  return {
    venues: query.data?.items ?? [],
    pagination: query.data
      ? {
          total: query.data.total,
          page: query.data.page,
          pageSize: query.data.page_size,
          totalPages: query.data.total_pages,
          hasNext: query.data.has_next,
          hasPrev: query.data.has_prev,
        }
      : null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    params,
    setSearch,
    setFilter,
    setFilters,
    setSort,
    setPage,
    resetFilters,
    setAllParams,
    refetch: query.refetch,
  };
}
